import { useMemo } from "react";
import { useTaskState } from "./use-task-state";
import { TaskType } from "@/components/tasks/TaskManager";

export interface PendingTaskItem {
  id: string;
  title: string;
  priority: "low" | "medium" | "high";
  dueDate?: string;
  isOverdue: boolean;
  isDueSoon: boolean;
}

export const usePendingTasks = () => {
  const { tasks, isLoading } = useTaskState();

  const pendingTasks = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysOut = new Date(today);
    sevenDaysOut.setDate(sevenDaysOut.getDate() + 7);

    return tasks
      .filter(task => task.status === "todo" || task.status === "in-progress")
      .map(task => {
        const due = task.dueDate ? new Date(task.dueDate) : null;
        const isOverdue = due ? due < today : false;
        const isDueSoon = due ? !isOverdue && due <= sevenDaysOut : false;
        return { ...task, isOverdue, isDueSoon, _due: due };
      })
      .sort((a, b) => {
        // Overdue first
        if (a.isOverdue && !b.isOverdue) return -1;
        if (!a.isOverdue && b.isOverdue) return 1;
        // Due soon next
        if (a.isDueSoon && !b.isDueSoon) return -1;
        if (!a.isDueSoon && b.isDueSoon) return 1;
        // Then by due date ASC
        if (a._due && b._due) return a._due.getTime() - b._due.getTime();
        if (a._due && !b._due) return -1;
        if (!a._due && b._due) return 1;
        return 0;
      })
      .slice(0, 5)
      .map(({ _due, ...rest }): PendingTaskItem => ({
        id: rest.id,
        title: rest.title,
        priority: rest.priority,
        dueDate: rest.dueDate,
        isOverdue: rest.isOverdue,
        isDueSoon: rest.isDueSoon,
      }));
  }, [tasks]);

  const count = useMemo(
    () => tasks.filter(t => t.status === "todo" || t.status === "in-progress").length,
    [tasks]
  );

  return { pendingTasks, count, isLoading };
};
